import React, { useCallback } from "react";
import type { Accessory } from "interfaces/product/accessory/accessory.types";
import { Button } from "components/Button";
import {
  useIsProductLoading,
  useProductOutput,
  useProductNodeId,
  useProductActions,
} from "stores/product";
import {
  useIsFetching as useIsFetchingTopologyAction,
  useTopology,
  useWizardActions,
} from "stores/wizardStore";
import { useTranslate } from "hooks";
import { updateTopologyItemOrderCode } from "utils";
import type { InvokeTopologyActionProps } from "interfaces/wizard/wizardStore.types";

export const UpdateConfigurationButton = ({
  removeModal,
  nodeId: nodeIdProp,
}: {
  removeModal: () => void;
  nodeId?: string;
}) => {
  const translate = useTranslate();
  const isProductLoading = useIsProductLoading();
  const isFetchingTopologyAction = useIsFetchingTopologyAction();
  const productOutput = useProductOutput();
  const { setTopology, invokeTopologyAction } = useWizardActions();
  const topology = useTopology();
  const nodeIdFromStore = useProductNodeId();
  const productActions = useProductActions();

  const nodeId = nodeIdProp || nodeIdFromStore;

  const handleConfigureProduct = useCallback(() => {
    const topologyItem = productActions.toPartialTopologyNode();
    if (!topology) {
      console.error("Topology is not available");
      return;
    }
    if (!topologyItem) {
      console.error("Topology item not updated");
      return;
    }
    if (!nodeId) {
      console.error("Node ID is not available");
      return;
    }

    if (!topologyItem.pimId) {
      console.error("pimId is not available");
      return;
    }

    setTopology(
      updateTopologyItemOrderCode({
        topology,
        id: nodeId,
        orderCode: topologyItem.orderCode,
        pimId: topologyItem.pimId as string,
        presentation: topologyItem.presentation,
        selectedAccessories: (topologyItem.selectedAccessories ??
          []) as Accessory[],
      }),
    );

    invokeTopologyAction({
      action: "configureComponent",
    } as InvokeTopologyActionProps).then(() => {
      removeModal();
    });
  }, [
    productActions,
    topology,
    nodeId,
    setTopology,
    invokeTopologyAction,
    removeModal,
  ]);

  return (
    <Button
      data-testid="ProductConfiguratorModal_update-configuration-btn"
      disabled={
        !productOutput?.product?.orderCode ||
        isFetchingTopologyAction ||
        isProductLoading
      }
      isLoading={
        !productOutput?.product?.orderCode ||
        isFetchingTopologyAction ||
        isProductLoading
      }
      className="m-0"
      onClick={handleConfigureProduct}
    >
      {translate("PRODUCT_CONFIGURATOR_UPDATE_CONFIGURATION")}
    </Button>
  );
};
